/* We separate out the *build-time* `process.env` processing from the *run-time* processing for a few reasons:
 *
 * 1. Type checking and schema enforcement needs to happen at build time
 * 2. We don't want/need to bundle zod in with the client JS
 */
import { CLIENT_ENV } from '@lib/env';

const isProduction = () => {
  return CLIENT_ENV.NEXT_PUBLIC_NODE_ENV === 'production';
};

// Note: the following will use the production app when running next.js as a
// production build. e.g. `next build && next start`
const getAppOrigin = () => {
  if (isProduction()) {
    return 'https://app.wistia.com';
  }
  return 'https://app.wistia.io';
};

export const APP_ORIGIN = getAppOrigin();

/** Canonical public origin for the marketing site (wistia.com). */
export const SITE_URL = 'https://wistia.com';

export const SUPPORT_URL = 'https://support.wistia.com/';

/**
 * Latest stable Wistia Desktop (macOS) installer, published by the glass
 * release pipeline. Link to /download/mac rather than this URL directly so the
 * visit (and its UTMs) is recorded before the download starts.
 */
export const WISTIA_DESKTOP_MAC_DMG_URL =
  'https://downloads.wistia.com/Wistia.dmg';

export const IS_PRODUCTION = isProduction();

export const GTM_ID = 'GTM-WJT32MJ';

export const EV1_URL = 'https://fast.wistia.com/assets/external/E-v1.js';

export const WISTIA_SESSION_COOKIE_NAME = `_w_session`;

export {
  WISTIA_GIT,
  WISTIA_GIT_PREMIUM,
  WISTIA_FREE_ACCOUNT,
  WISTIA_BUSINESS_TRIAL,
  WISTIA_BUSINESS_WEBINARS_BASIC_TRIAL,
  WISTIA_BUSINESS_WEBINARS_PLUS_TRIAL,
  WISTIA_BUSINESS_AUTOMATION_TRIAL,
  WISTIA_DEVELOPER_ACCOUNT,
} from '@lib/constants/registration';

export const HUBSPOT_PORTAL_ID = '120237';

export const BLOG_SUBSCRIBE_FORM_ID = '0b0b5d78-0af4-415c-a2b5-aade9ef2b096';

// reCAPTCHA v3 action minted into the token on the client and asserted by the
// server verifier; shared so the two sides cannot drift apart.
export const BLOG_SUBSCRIBE_RECAPTCHA_ACTION = 'blog_subscribe';

// The Sentry tag vocabulary for this path (component, reasons, tag factory) lives
// in `@lib/blog-subscribe-telemetry` — it is a factory and a checked union, not a
// constant.

// HubSpot form name: [REPORT] State of Video 2025 - Updated
export const HUBSPOT_STATE_OF_VIDEO_2025_FORM_ID =
  'defac7ea-15d4-4a4a-9202-4934b6d0712e';

// HubSpot form name: [Sales] Premium Plan - GIT Request (Website) - Updated [white text for Storefront testing]
// This form has white text meant for a dark background
export const HUBSPOT_TEST_FORM_WHITE_TEXT_ID =
  'ee852e96-947c-4c5f-8b93-6befeb204f7e';

// HubSpot form name: [Sales] Premium Plan - GIT Request (Website) - Updated
export const HUBSPOT_TEST_FORM_ID = 'd167e04c-0205-4f19-81ec-3c55d1d9c5b5';

// HubSpot form name: [Sales] Premium Plan - GIT Request (Website)
export const HUBSPOT_GIT_SALES_FORM_ID = 'c59d4834-0b02-4275-aac0-6cc1e7599395';

// HubSpot form name: [Marketing] Webinar Guidebook LP - Updated
export const HUBSPOT_WEBINAR_GUIDEBOOK_FORM_ID =
  'c487b8c1-9adf-421e-bca9-f9487ebdc134';

// HubSpot form name: [Sales] Premium Plan - GIT Request (Website) - Updated
export const HUBSPOT_GIT_SALES_FORM_UPDATED_ID =
  'd167e04c-0205-4f19-81ec-3c55d1d9c5b5';

// HubSpot form name: Request an Integration Form
// This form has white text meant for a dark background
export const HUBSPOT_REQUEST_INTEGRATION_FORM_ID =
  'eb403831-5c70-4b92-b76f-59c60061baf2';

// HubSpot form name: Blog Subscribe
export const HUBSPOT_BLOG_SUBSCRIBE_FORM_ID =
  '0b0b5d78-0af4-415c-a2b5-aade9ef2b096';

// HubSpot form name: Privacy & Data Request
export const HUBSPOT_PRIVACY_DATA_REQUEST_FORM_ID =
  'e85e862c-3daa-4ed1-bb96-7531eb1bab48';

// HubSpot form that emails the visitor the Wistia Desktop (macOS) installer
// link, for people who land on a Mac download CTA from a phone or tablet.
export const HUBSPOT_MAC_DOWNLOAD_LINK_FORM_ID =
  'a405b2a5-4153-49fc-94d9-4f470ff28ff7';

export const CHROMATIC_DEFAULT_DELAY = 500;
export const CHROMATIC_VIDEO_DELAY = 3000;
/**
 * ## Recommended Thresholds by Component Type
 * | Component Type | Recommended `diffThreshold` | Rationale |
 * |----------------|----------------------------|-----------|
 * | Static UI (buttons, cards) | `0.0` - `0.1` | Should be pixel-perfect |
 * | Images with filters | `0.1` - `0.2` | Minor compression differences |
 * | Videos | `0.3` - `0.4` | Player controls, thumbnails vary |
 * | Charts/Graphs | `0.2` - `0.3` | Data visualization quirks |
 * | Animations (paused) | `0.1` - `0.2` | Anti-aliasing differences |
 * | Complex layouts | `0.15` - `0.25` | Multiple dynamic elements |
 */
export const CHROMATIC_DEFAULT_DIFF_THRESHOLD = 0.1; // Chromatic's default is .063
export const CHROMATIC_IMAGE_DIFF_THRESHOLD = 0.15;
export const CHROMATIC_VIDEO_DIFF_THRESHOLD = 0.35; // video player is external package, and often has updated UI changes
export const CHROMATIC_EXTERNAL_EMBED_DIFF_THRESHOLD = 0.5; // third-party embeds (YouTube player, X tweet card) render live external content that drifts between snapshots

/**
 * Height of the main navigation in px, for anything fixed or sticky that sits
 * directly beneath it: the mobile nav drawer and the mobile pricing controls.
 */
export const MOBILE_NAVBAR_OFFSET_DEFAULT = 60;

export { buildMetaImageUrl } from '@lib/social-card';

export const GBUILDS = String.raw`


  __                                                                    __   __   __   __ 
|/  |         | /           /            |       |                     /  ) /  | /  ) /   
|   | ___  ___|   ___  ___ (___  ___  ___|       |      ___  ___      (  / (   |(  / |___ 
|   )|___)|   )| |    |   )|    |___)|   )       )|   )|   )|___)       / )|   )  / )    )
|__/ |__  |__/ | |__  |__/||__  |__  |__/      _/ |__/ |  / |__        /_/ |__/  /_/  __/ 

                                                                                          

  ██████╗  ███████╗ ██████╗ ██████╗ ██╗███████╗    ███████╗███╗   ███╗██╗████████╗██╗  ██╗
  ██╔════╝ ██╔═══██╗██╔══██╗██╔══██╗██║██╔════╝    ██╔════╝████╗ ████║██║╚══██╔══╝██║  ██║
  ██║  ███╗██║   ██║██████╔╝██║  ██║██║█████╗      ███████╗██╔████╔██║██║   ██║   ███████║
  ██║   ██║██║   ██║██╔══██╗██║  ██║██║██╔══╝      ╚════██║██║╚██╔╝██║██║   ██║   ██╔══██║
  ╚██████╔╝╚██████╔╝██║  ██║██████╔╝██║███████╗    ███████║██║ ╚═╝ ██║██║   ██║   ██║  ██║
   ╚═════╝  ╚═════╝ ╚═╝  ╚═╝╚═════╝ ╚═╝╚══════╝    ╚══════╝╚═╝     ╚═╝╚═╝   ╚═╝   ╚═╝  ╚═╝
                                                 
  
    
   ___                _    __                                           __                                     
  /                  /|   /  |                                         /         /         / /    | /          
(___  ___  ___      ( |  (   |           ___  ___  ___  ___       ___ (         (___        (  ___|   ___  ___ 
|    |   )|   )       | )|   )     \   )|___)|   )|   )|___      |   )|___      |   )|   )| | |   )| |   )|   )
|    |__/ |          _|/ |__/       \_/ |__  |__/||     __/      |__/ |         |__/ |__/ | | |__/ | |  / |__/ 
                                     /                                                                    __/ 
                                     
    

    `;
